import React, { useEffect, useRef } from 'react';
import { useColorModeValue, usePrefersReducedMotion } from '@chakra-ui/react';

import { ChargerStatus } from 'clipsal-cortex-types/src/api/api-evcharger';

import { COLOR_INDICATOR_DEFAULT, COLOR_INDICATOR_DEFAULT_DARK } from '../constants';
import { EvseType } from '../types';
import { ChargingCable, EV, EVSECharger, PowerCordState, SmartPlugPowerCord } from './animation-objects';

interface Props {
  status: ChargerStatus;
  isFirmwareMode?: boolean;
  isFirmwareInstalling?: boolean;
  isReducedSpeed?: boolean;
  isChargingStopped?: boolean;
  isTemperatureIncreased?: boolean;
  evseType?: EvseType;
}

const IMPULSE_COUNT = 7; // Number of charging impulses to show in the animation
const IMPULSE_RADIUS = 5; // 10×10 px diameter
const IMPULSE_DURATION_MS = 3000; // time for one impulse to traverse the full cable

export const StatusAnimation = ({
  status,
  isFirmwareMode = false,
  isFirmwareInstalling = false,
  isReducedSpeed = false,
  isChargingStopped = false,
  isTemperatureIncreased = false,
  evseType = 'SmartAmp',
}: Props) => {
  const isPlugged = ['Charging', 'SuspendedEV', 'SuspendedEVSE', 'Faulted', 'Finishing', 'Preparing'].includes(status);
  const powerCordState: PowerCordState = isChargingStopped
    ? 'stopped'
    : isTemperatureIncreased && status === 'Charging'
    ? 'slowed'
    : 'idle';
  const isSmartPlugStopped = evseType === 'SmartPlug' && isChargingStopped;
  const cablePathRef = useRef<SVGPathElement | null>(null);
  const impulseRefs = useRef(Array.from({ length: IMPULSE_COUNT }, () => React.createRef<SVGCircleElement>()));
  const impulseColor = useColorModeValue(COLOR_INDICATOR_DEFAULT, COLOR_INDICATOR_DEFAULT_DARK);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    // Fade out all impulses whenever status changes
    impulseRefs.current.forEach((ref) => {
      if (ref.current) ref.current.setAttribute('opacity', '0');
    });

    if (status !== 'Charging') return;

    const pathEl = cablePathRef.current;
    if (!pathEl) return;

    const totalLength = pathEl.getTotalLength();
    let rafId: number;
    const duration = IMPULSE_DURATION_MS + (isReducedSpeed ? 1500 : 0);
    let startTime: number | null = null;

    function animate(timestamp: number) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      impulseRefs.current.forEach((ref, i) => {
        const el = ref.current;
        if (!el) return;
        // Stagger each impulse evenly along the path
        const t = (elapsed / duration + i / IMPULSE_COUNT) % 1;
        const point = pathEl!.getPointAtLength(t * totalLength);
        el.setAttribute('cx', String(point.x));
        el.setAttribute('cy', String(point.y));
        el.setAttribute('opacity', '1');
      });

      rafId = requestAnimationFrame(animate);
    }

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [status, isReducedSpeed]);

  return (
    <svg width="100%" height="100%" viewBox="-11 0 327 290" fill="none" preserveAspectRatio="xMidYMid meet">
      <EVSECharger chargerStatus={status} isFirmwareMode={isFirmwareMode} isStopped={isSmartPlugStopped} />
      <EV
        chargerStatus={status}
        isPlugged={isPlugged}
        reducedMotion={prefersReducedMotion}
        isFirmwareInstalling={isFirmwareInstalling}
        isStopped={isSmartPlugStopped}
      />
      {evseType === 'SmartPlug' && <SmartPlugPowerCord state={powerCordState} reducedMotion={prefersReducedMotion} />}
      <ChargingCable
        cablePathRef={cablePathRef}
        isPlugged={isPlugged}
        reducedMotion={prefersReducedMotion}
        evseType={evseType}
      />
      {impulseRefs.current.map((ref, i) => (
        <circle
          key={i}
          ref={ref}
          data-testid="charging-impulse"
          r={IMPULSE_RADIUS}
          fill={impulseColor}
          opacity={0}
          style={prefersReducedMotion ? undefined : { transition: 'opacity 0.4s ease-in-out' }}
        />
      ))}
    </svg>
  );
};
